import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Flex, List, message, Space, Tag } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import {
  CustomButton,
  CustomModalWithoutButton,
  SearchInput,
} from '../../../components';
import EllipsisTooltip from '../../../components/EllipsisTooltip';
import StepsHeading from './StepsHeading';
import ConnectsSensors from '../../Modals/ConnectsSensors';
import { useSpin } from '../../../components/SpinContext';
import { DeleteSensor, GetSensors } from '../../../apis/onboarding';
import useToken from '../../../common/helper/useToken';
import { getProviderImagePath } from '../../../common/utils/imageUtils';
import { processSensorList } from '../../../common/utils/sensorUtils';
import ZLShield from '../../../assets/images/ZLshield.png';
import Arrows from '../../../assets/images/arrows.png';
import useUnauthorize from '../../../hooks/useUnauthorize';
import { statusConfig } from '@/components/StatusTag';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';
import { LinkInlineIcon } from '../../../assets/icons/icons';

interface CompanySensor {
  id: string;
}

interface SensorItem {
  id: string;
  name?: string;
  sensor_name?: string;
  // TODO: is description a string or a Record<string, string>[]?
  description?: string;
  status?: string;
  is_enabled?: boolean;
  company_sensor?: CompanySensor;
  required_inputs?: string[];
  credential_description?: unknown[];
}

/**
 * Sensors onboarding step component
 *
 * Allows users to connect their sensor providers during the onboarding process.
 * Users can search, link, and disconnect sensor accounts.
 */
const Sensors: React.FC = () => {
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const { token } = useToken();
  const authToken = token ?? '';
  const { unauthorize } = useUnauthorize();
  const { showSpin, hideSpin } = useSpin();
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [list, setList] = useState<SensorItem[]>([]);
  const [sensorData, setSensorData] = useState<SensorItem | null>(null);

  const processedAndFilteredList = useMemo(() => {
    if (!list) return [];
    const processed = processSensorList(list);
    if (!searchQuery) {
      return processed;
    }
    return processed.filter((provider) =>
      provider?.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [list, searchQuery]);

  const handleSuccess = () => {
    fetchData();
    setModalOpen(false);
    setSensorData(null);
  };

  const fetchData = useCallback(() => {
    showSpin();
    GetSensors(authToken)
      .then((res) => {
        if (res) {
          const data = res?.data || null;
          if (data && data?.data?.is_success) {
            setList(data.data.data || []);
          } else {
            message.error(data?.data?.message || 'Failed to fetch sensors.');
          }
        }
      })
      .catch(unauthorize)
      .finally(hideSpin);
  }, [hideSpin, showSpin, authToken, unauthorize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleButtonClick = (item: SensorItem): void => {
    if (item && item.id && item.name) {
      setSensorData(item);
      setModalOpen(true);
    } else {
      console.error('Invalid item passed to handleButtonClick', item);
    }
  };

  const handleDelete = (item: SensorItem): void => {
    if (item && item?.company_sensor?.id) {
      showSpin();
      DeleteSensor(item.company_sensor.id, authToken)
        .then((res) => {
          const data = res?.data || null;
          if (data && data?.data?.is_success) {
            message.success(data.data.message);
            fetchData();
          } else {
            message.error(data?.data?.message || 'Failed to delete sensor.');
          }
        })
        .catch(unauthorize)
        .finally(hideSpin);
    }
  };

  return (
    <>
      <div className='default-steps-content onboarding-sensors-step'>
        <StepsHeading
          heading='Connect your sensors'
          text='Connect your sensors so we can help you monitor the conditions of your shipments.'
        />

        <SearchInput
          placeholder={`Search for a ${themeT('sensor', theme.themeType).toLowerCase()}...`}
          width='min(420px, 100%)'
          defaultValue={searchQuery}
          onChange={setSearchQuery}
          style={{ height: '36px' }}
        />
        <List
          className='custom-list-container'
          itemLayout='horizontal'
          dataSource={processedAndFilteredList}
          renderItem={(item) => (
            <List.Item className='onboarding-sensor-list-item'>
              <div className='onboarding-sensor-info'>
                <Avatar
                  className='onboarding-sensor-avatar'
                  src={
                    <img
                      src={getProviderImagePath(item.name, 'SensorImages')}
                      alt='avatar'
                      className='onboarding-courier-img'
                    />
                  }
                />
                <Space className='onboarding-sensor-title'>
                  <EllipsisTooltip
                    className='onboarding-sensor-name'
                    text={item.name || ''}
                  />
                  {item.is_enabled && item.status === 'Connected' && (
                    <Tag
                      color='success'
                      icon={
                        <CheckOutlined className='onboarding-courier-icon-success' />
                      }
                      style={statusConfig.Synced}
                    >
                      Synced
                    </Tag>
                  )}
                </Space>
              </div>
              {item.status !== 'Connected' ? (
                <CustomButton
                  className='onboarding-sensor-link-btn'
                  onClick={() => handleButtonClick(item)}
                  height='36px'
                  fontSize='14px'
                  text='Link account'
                  bgColor='var(--link-btn-bg)'
                  borderRadius='8px'
                  color='var(--text-primary)'
                  boxShadow='none'
                  iconPosition='left'
                  icon={<LinkInlineIcon />}
                  gap='10px'
                  fontWeight={500}
                />
              ) : (
                <div className='onboarding-sensor-icon-action'>
                  <CloseOutlined
                    className='onboarding-courier-icon-muted'
                    onClick={() => handleDelete(item)}
                  />
                </div>
              )}
            </List.Item>
          )}
        />

        {modalOpen && sensorData && (
          <CustomModalWithoutButton
            modalOpen={modalOpen}
            setModalOpen={(value) => {
              setModalOpen(value);
              if (!value) setSensorData(null); // Clear data when closing
            }}
            modalTitle={`Connect to ${sensorData.name}`}
            modalText="By providing API access to Zoomlogi, you're enabling Zoomlogi to retrieve your sensor information."
            modalSize={795}
            modalBorderRadius={'16px'}
            headerIcon={
              <Flex align='center' justify='center' gap={12}>
                <Avatar
                  src={
                    <img
                      src={getProviderImagePath(
                        sensorData.name,
                        'SensorImages'
                      )}
                      alt={sensorData.name}
                      className='onboarding-courier-img'
                    />
                  }
                  size={52}
                  className='onboarding-courier-logo-avatar'
                />
                <img
                  src={Arrows}
                  alt='connects to'
                  className='onboarding-courier-arrows'
                />
                <Avatar size={52} className='onboarding-courier-logo-avatar'>
                  <div className='onboarding-courier-mark'>
                    <img
                      src={ZLShield}
                      alt='Zoomlogi'
                      className='onboarding-courier-img'
                    />
                  </div>
                </Avatar>
              </Flex>
            }
          >
            <ConnectsSensors
              closeModal={handleSuccess}
              id={sensorData.id}
              /* @ts-expect-error TODO fix types */
              sensorData={sensorData}
              onSuccess={handleSuccess}
            />
          </CustomModalWithoutButton>
        )}
      </div>
    </>
  );
};

export default Sensors;
